import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { OrderView } from "../components/publicOrder/OrderView";
import { PageHeader } from "../components/shell/PageHeader";
import { EmptyState, ErrorNotice, SkeletonRows } from "../components/ui/Feedback";
import { Freshness } from "../components/ui/Freshness";
import { SearchField, SelectField } from "../components/ui/Input";
import { RowState } from "../components/ui/Status";
import type { Column } from "../components/ui/Table";
import { Cell, Pagination, TableFrame, TableHead, TableRow } from "../components/ui/Table";
import { Metric, MicroLabel } from "../components/ui/Text";
import { get, getPage } from "../lib/api";
import { useViewParam } from "../lib/useViewParam";
import { count, since, timestamp } from "../lib/format";
import {
  ORDER_STATE_FILTERS,
  orderMoney,
  orderOutcome,
  orderStateTone,
  queueAge,
  queueTotal,
  queueTotalHint,
} from "../lib/publicOrder";
import { PUBLIC_ORDER_STATES, type PublicOrder, type RefundQueueSummary } from "../lib/types";

/**
 * Public charging, as the operator's ledger. pay-ui spec §9.5.
 *
 * Until this page existed the only way to answer "who are we holding money for"
 * was `psql` — which is how the two orders currently owed a refund on this
 * deployment were found, and it is why `routes/publicOrderViews.ts` was written.
 * The table is the other half: it turns a silent debt into a known one.
 *
 * ## The masthead is the refund queue, not the table
 *
 * §9.5: "Alert on the queue's age, not its size. A queue of 40 refunds cleared
 * daily is healthy; one refund sitting for a week is somebody's money." So the
 * oldest debt is a metric in its own right and it is the one that changes colour,
 * while the count sits beside it without shouting.
 *
 * Those figures come from their own endpoint rather than from the rows below,
 * because they must not depend on which page is open — a queue three pages deep
 * summarised from twenty visible rows is a fact about pagination.
 *
 * ## Read-only, and that is the current state of §9.5 rather than a design choice
 *
 * The *mark refunded* action writes a `payment_transaction` row and belongs on the
 * same path the automated refund worker will use, so it is not an edit to an order
 * and it is not here yet. Everything on this page is therefore a link or a filter.
 *
 * ## Weighted columns
 *
 * State leads, as a pill only when it asks for attention (`RowState`). The
 * outcome sentence is the row's meaning and reads at body weight. Of the money,
 * what was taken is the heavy figure and what is owed back is the loud one; the
 * unused figure and the creation time recede. A row opens the order over this
 * list (`?order=`), so the filters survive the look.
 */

const COLUMNS: readonly Column[] = [
  { key: "state", label: "State", width: "11%" },
  { key: "outcome", label: "Outcome" },
  { key: "station", label: "Charge point", width: "14%" },
  /**
   * "Taken", not "Paid". On a deployment that has switched to holds both flows
   * appear in this table at once, and a header saying "Paid" would be false on
   * every held row — the per-row caption is in `orderMoney().takenLabel`.
   */
  { key: "taken", label: "Taken", numeric: true, width: "10%" },
  { key: "owed", label: "Owed", numeric: true, width: "9%" },
  { key: "unused", label: "Unused", numeric: true, width: "8%" },
  { key: "created", label: "Created", width: "13%" },
];

/**
 * Faster than the sessions table's cadence. These rows carry money in flight —
 * a `pending` hold lapses on `PUBLIC_HOLD_TTL_S`, and a settlement can land at any
 * moment — so the page is worth keeping close to live.
 */
const POLL_MS = 10_000;

/** Past this, the oldest debt is drawn as a signal rather than as a figure. §9.5. */
const STALE_QUEUE_MS = 24 * 60 * 60 * 1000;

export function PublicOrders() {
  const [params, setParams] = useSearchParams();
  const [viewing, setViewing] = useViewParam("order");

  const chargePointId = params.get("chargePointId") ?? "";
  const state = params.get("state") ?? "";
  const owing = params.get("owing") ?? "";
  /**
   * §20.7 — arrived at from a customer's row, never typed. Kept out of the filter
   * form for that reason: an operator has no way to know a uuid, and a text box
   * they cannot fill is a dead control. It appears instead as the banner below,
   * which says who the table is narrowed to and how to leave.
   */
  const customerId = params.get("customerId") ?? "";
  const page = positiveInt(params.get("page")) ?? 1;

  const orders = useQuery({
    queryKey: ["public-orders", { chargePointId, state, owing, customerId, page }],
    queryFn: () =>
      getPage<PublicOrder>("/public-orders", {
        query: { chargePointId, state, owing, customerId, page },
      }),
    refetchInterval: POLL_MS,
    placeholderData: (previous) => previous,
  });

  /**
   * Its own query, on its own cache key: the summary is a fact about the whole
   * queue and must not be refetched or invalidated by a filter change below.
   */
  const queue = useQuery({
    queryKey: ["public-orders", "refund-queue"],
    queryFn: () => get<RefundQueueSummary>("/public-orders/refund-queue"),
    refetchInterval: POLL_MS,
  });

  const rows = orders.data?.rows ?? [];
  const now = Date.now();

  const summary = queue.data;
  const oldest = summary?.oldestOwedAt ?? null;
  const isStale = oldest !== null && now - Date.parse(oldest) > STALE_QUEUE_MS;

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value === "") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next, { replace: true });
  }

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Public charging"
        metrics={
          <>
            <Metric
              label="Refunds owed"
              value={count(summary?.owedOrders)}
              tone={(summary?.owedOrders ?? 0) > 0 ? "signal" : "muted"}
              hint="across every station"
            />
            <Metric
              label="Total owed"
              value={queueTotal(summary, rows)}
              hint={queueTotalHint(rows)}
            />
            <Metric
              label="Oldest debt"
              value={queueAge(oldest, now)}
              tone={isStale ? "signal" : "muted"}
              hint={oldest === null ? "nothing outstanding" : "waiting to be returned"}
            />
          </>
        }
        freshness={
          <Freshness
            updatedAt={orders.dataUpdatedAt}
            isFetching={orders.isFetching}
            intervalMs={POLL_MS}
          />
        }
        lede="Most public sessions end owing money back — a driver buys a budget and rarely spends all of it. A refund waiting a week is somebody's money, so the queue's age matters more than its length."
      />

      <form
        onSubmit={(event) => event.preventDefault()}
        className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2 lg:grid-cols-3"
      >
        <SearchField
          label="Charge point"
          placeholder="Exact charge point id"
          value={chargePointId}
          onChange={(event) => setFilter("chargePointId", event.target.value)}
          hint="The station id as it connects, not a search."
        />
        <SelectField
          label="State"
          value={state}
          onChange={(event) => setFilter("state", event.target.value)}
        >
          {/* Exactly the values `public_charge_order_state_check` permits, in §8's
              own order. The server refuses an unknown one rather than ignoring it,
              so a filter that can never match is a 400 here rather than a silent
              full table — and one this list omits is a search that finds nothing
              for orders that exist. */}
          <option value="">Any</option>
          {PUBLIC_ORDER_STATES.map((value) => (
            <option key={value} value={value}>
              {ORDER_STATE_FILTERS[value]}
            </option>
          ))}
        </SelectField>
        <SelectField
          label="Money owed"
          value={owing}
          onChange={(event) => setFilter("owing", event.target.value)}
        >
          <option value="">Any</option>
          <option value="true">Owing a refund</option>
        </SelectField>
      </form>

      {customerId === "" ? null : (
        <MicroLabel>
          Showing one customer's charges.{" "}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-ink"
            onClick={() => setFilter("customerId", "")}
          >
            Show every order
          </button>
        </MicroLabel>
      )}

      {orders.isError ? (
        <ErrorNotice error={orders.error} onRetry={() => void orders.refetch()} />
      ) : null}

      <div className="flex flex-col gap-3">
        <TableFrame>
          <TableHead columns={COLUMNS} />
          {orders.isPending ? (
            <SkeletonRows rows={8} columns={COLUMNS.length} />
          ) : (
            <tbody>
              {rows.map((row, index) => {
                const figures = orderMoney(row);
                const owes = row.refundOwedMinor !== "0";

                return (
                  <TableRow
                    key={row.id}
                    index={index}
                    onActivate={() => setViewing(row.id)}
                    // Reserved for money taken and not yet returned, which is the
                    // only thing on this page an operator has to act on.
                    isFaulted={owes}
                  >
                    <Cell>
                      <RowState tone={orderStateTone(row.state)}>{row.state}</RowState>
                    </Cell>
                    <Cell className={owes ? "font-medium" : ""}>{orderOutcome(row)}</Cell>
                    <Cell>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-ink">{row.chargePointId}</span>
                        <span className="text-micro tracking-normal normal-case text-ink-faint">
                          EVSE {row.evseId}
                        </span>
                      </div>
                    </Cell>
                    {/*
                      Muted on the figure rather than on `settledAt`: a held order
                      is settled and has still taken nothing, so keying the emphasis
                      on settlement would make an em dash look like a real amount.
                    */}
                    <Cell numeric>
                      {figures.taken === "—" ? (
                        <span className="text-ink-faint">—</span>
                      ) : (
                        <span className="font-display text-section font-semibold tabular-nums text-ink">
                          {figures.taken}
                        </span>
                      )}
                    </Cell>
                    <Cell numeric>
                      {owes ? (
                        <span className="font-display text-section font-semibold tabular-nums text-signal">
                          {figures.owed}
                        </span>
                      ) : (
                        <span className="text-ink-faint">—</span>
                      )}
                    </Cell>
                    <Cell numeric muted className="tabular-nums">
                      {figures.unused}
                    </Cell>
                    <Cell>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-ink-muted">
                          {timestamp(row.createdAt, row.depotTimezone)}
                        </span>
                        <span className="text-micro tracking-normal normal-case text-ink-faint">
                          {since(row.createdAt, now)}
                        </span>
                      </div>
                    </Cell>
                  </TableRow>
                );
              })}
            </tbody>
          )}
        </TableFrame>

        {!orders.isPending && rows.length === 0 ? (
          <EmptyState
            title="No public orders match these filters."
            detail="An order appears the moment a driver scans a station and chooses an amount, before any money has moved."
          />
        ) : null}

        {rows.some((row) => row.refundOwedMinor !== "0") ? (
          <MicroLabel>A marked row is holding money that has not been returned.</MicroLabel>
        ) : null}

        <Pagination
          meta={orders.data?.meta}
          shown={rows.length}
          onPage={(next) => {
            const nextParams = new URLSearchParams(params);
            if (next <= 1) nextParams.delete("page");
            else nextParams.set("page", String(next));
            setParams(nextParams);
          }}
        />
      </div>

      <OrderView
        orderId={viewing}
        open={viewing !== ""}
        onOpenChange={(open) => {
          if (!open) setViewing("");
        }}
      />
    </>
  );
}

function positiveInt(raw: string | null): number | undefined {
  if (raw === null || !/^\d+$/.test(raw)) return undefined;
  const value = Number(raw);
  return value >= 1 ? value : undefined;
}
